import { ApiError, type TeamMember, type TeamSummary } from '../api';

export type OrgRole = TeamMember['role'];

/**
 * Ordered high-privilege-first, matching `VALID_ORG_ROLES` in
 * `collabv3/src/teams.ts`. The server is the authority: it re-derives this rank
 * on every invite and rejects anything above the caller's own role. Mirroring
 * it here only keeps the picker from offering a choice that is guaranteed to
 * 403 -- it is never the check.
 */
export const ORG_ROLES: readonly OrgRole[] = ['owner', 'admin', 'member', 'viewer', 'guest'];

function orgRoleRank(role: string): number {
  const index = ORG_ROLES.indexOf(role as OrgRole);
  return index === -1 ? -1 : ORG_ROLES.length - index;
}

export function canManageMembers(callerRole: string): boolean {
  return callerRole === 'owner' || callerRole === 'admin';
}

/**
 * Roles this caller may hand out. Empty for a non-admin, so the UI has one
 * source of truth for "can I invite at all" and "at what level".
 */
export function assignableRoles(callerRole: string): OrgRole[] {
  if (!canManageMembers(callerRole)) return [];
  const ceiling = orgRoleRank(callerRole);
  return ORG_ROLES.filter((role) => orgRoleRank(role) <= ceiling);
}

/**
 * Stytch reports a member who has been sent a magic-link invite but has never
 * completed it as anything other than `active` (`invited` and `pending` are
 * both observed). Treating "not active" as outstanding keeps a new Stytch
 * status from silently filing a stranger into the roster as a joined teammate.
 */
export function isPendingInvitation(member: TeamMember): boolean {
  return member.status.trim().toLowerCase() !== 'active';
}

export interface MembershipPartition {
  active: TeamMember[];
  pending: TeamMember[];
}

export function partitionMembership(members: readonly TeamMember[]): MembershipPartition {
  const active: TeamMember[] = [];
  const pending: TeamMember[] = [];
  for (const member of members) (isPendingInvitation(member) ? pending : active).push(member);
  return { active, pending };
}

export interface MemberIdentity {
  /** What to render as the row's headline. */
  title: string;
  /** Secondary line, or null when it would only repeat the title. */
  subtitle: string | null;
  /** One or two letters for the avatar. */
  initials: string;
}

/**
 * A member with no Stytch profile name has only an email, and printing it as
 * both the name and the subtitle -- which the first version of this surface did
 * -- reads as a rendering bug. Fall back to the email as the single line rather
 * than inventing a name out of its local part, which guesses wrong on shared
 * and role addresses.
 */
export function memberIdentity(member: TeamMember): MemberIdentity {
  const name = member.name.trim();
  const email = member.email.trim();
  const title = name || email || member.memberId;
  const subtitle = name && email ? email : null;
  return { title, subtitle, initials: initialsFor(name, email) };
}

function initialsFor(name: string, email: string): string {
  const words = name.split(/\s+/).filter(Boolean);
  if (words.length >= 2) return (words[0]![0]! + words[1]![0]!).toUpperCase();
  const single = words[0] || email;
  return single.slice(0, 2).toUpperCase() || '?';
}

/**
 * Every whitespace-separated token has to match somewhere, so "greg admin"
 * narrows rather than widening the way a single substring test would.
 */
export function searchMembers(members: readonly TeamMember[], term: string): TeamMember[] {
  const tokens = term.trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return [...members];
  return members.filter((member) => {
    const haystack = `${member.name} ${member.email} ${member.role}`.toLowerCase();
    return tokens.every((token) => haystack.includes(token));
  });
}

export type MemberSortKey = 'name' | 'role' | 'joined';

export interface MemberSort {
  key: MemberSortKey;
  direction: 'asc' | 'desc';
}

export const DEFAULT_MEMBER_SORT: MemberSort = { key: 'name', direction: 'asc' };

export const MEMBER_SORT_LABELS: Record<MemberSortKey, string> = {
  name: 'Name',
  role: 'Role',
  joined: 'Joined',
};

/**
 * Sorts on the *rendered* title, not the raw name: a nameless member sorts by
 * the email actually on screen instead of collapsing to the top of the list
 * behind an empty string.
 */
export function sortMembers(members: readonly TeamMember[], sort: MemberSort): TeamMember[] {
  const factor = sort.direction === 'asc' ? 1 : -1;
  return [...members].sort((a, b) => {
    const primary = compareOn(a, b, sort.key);
    if (primary !== 0) return primary * factor;
    // Stable, host-independent tiebreak so two members who compare equal on the
    // chosen key do not reorder between renders.
    return memberIdentity(a).title.localeCompare(memberIdentity(b).title) * factor;
  });
}

function compareOn(a: TeamMember, b: TeamMember, key: MemberSortKey): number {
  if (key === 'role') return orgRoleRank(b.role) - orgRoleRank(a.role);
  if (key === 'joined') return parseJoined(a.createdAt) - parseJoined(b.createdAt);
  return memberIdentity(a).title.localeCompare(memberIdentity(b).title, undefined, { sensitivity: 'base' });
}

/** An unparseable or absent join date sorts last rather than to 1970. */
function parseJoined(value: string): number {
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? Number.POSITIVE_INFINITY : parsed;
}

// ── Invitations ────────────────────────────────────────────────

export type InviteFailureReason = 'not-permitted' | 'already-member' | 'invalid-email' | 'unknown';

export interface InviteFailure {
  reason: InviteFailureReason;
  message: string;
}

/**
 * Turns the invite endpoint's failures into something an admin can act on. The
 * two that are genuinely recoverable -- the invitee is already here, and the
 * role is above the caller's own -- otherwise arrive as a bare 403/400 and read
 * as "the console is broken".
 */
export function describeInviteFailure(cause: unknown): InviteFailure {
  const raw = cause instanceof Error ? cause.message : '';
  const detail = raw.trim();
  const lower = detail.toLowerCase();
  if (lower.includes('duplicate') || lower.includes('already') || lower.includes('member_already')) {
    return { reason: 'already-member', message: 'That address already belongs to this organization.' };
  }
  if (cause instanceof ApiError && cause.status === 403) {
    return {
      reason: 'not-permitted',
      message: detail || 'You do not have permission to invite at that role.',
    };
  }
  if (lower.includes('email')) {
    return { reason: 'invalid-email', message: detail || 'That email address was rejected.' };
  }
  return { reason: 'unknown', message: detail || 'The invitation could not be sent.' };
}

/**
 * Splits a pasted list into addresses. Admins onboard a team from a spreadsheet
 * cell or a chat message, so commas, semicolons, newlines and stray whitespace
 * all have to mean the same thing.
 */
export function parseInviteEmails(input: string): string[] {
  const seen = new Set<string>();
  const emails: string[] = [];
  for (const candidate of input.split(/[\s,;]+/)) {
    const email = candidate.trim().toLowerCase();
    if (!email) continue;
    if (seen.has(email)) continue;
    seen.add(email);
    emails.push(email);
  }
  return emails;
}

/** Deliberately permissive: the server and Stytch reject for real. */
export function looksLikeEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

export interface InviteBatchResult {
  sent: string[];
  failed: Array<{ email: string; failure: InviteFailure }>;
}

/**
 * Failure-only rendering, for the invite popover: the panel banner already
 * reports what was sent, so repeating it inside the still-open form reads as
 * two contradictory answers to one action.
 */
export function describeInviteFailures(result: InviteBatchResult): string {
  if (result.failed.length === 0) return '';
  if (result.failed.length === 1) {
    const only = result.failed[0]!;
    return `${only.email}: ${only.failure.message}`;
  }
  return result.failed.map((entry) => `${entry.email}: ${entry.failure.message}`).join(' ');
}

export function summarizeInviteBatch(result: InviteBatchResult): string {
  const parts: string[] = [];
  if (result.sent.length === 1) parts.push(`Invitation sent to ${result.sent[0]}.`);
  else if (result.sent.length > 1) parts.push(`${result.sent.length} invitations sent.`);
  if (result.failed.length === 1) {
    const only = result.failed[0]!;
    parts.push(`${only.email}: ${only.failure.message}`);
  } else if (result.failed.length > 1) {
    parts.push(`${result.failed.length} addresses could not be invited.`);
  }
  return parts.join(' ');
}

// ── Projects ───────────────────────────────────────────────────

export type TeamProject = TeamSummary['projects'][number];

/**
 * Pulls `owner/repo` out of any of the remote spellings the desktop stores --
 * `git@host:owner/repo.git`, `https://host/owner/repo.git`,
 * `ssh://git@host/owner/repo` -- and returns just the repository segment.
 */
export function repositoryNameFromRemote(remoteUrl: string | null | undefined): string | null {
  if (!remoteUrl) return null;
  // Trailing slashes come off first: a remote stored as `…/collab.git/` would
  // otherwise keep its `.git` suffix and name the project "collab.git".
  const withoutSuffix = remoteUrl.trim().replace(/\/+$/, '').replace(/\.git$/i, '');
  if (!withoutSuffix) return null;
  const segments = withoutSuffix.split(/[/:]/).filter(Boolean);
  const last = segments[segments.length - 1];
  if (!last || last.includes('@')) return null;
  return last;
}

/**
 * The overview used to print a raw project UUID whenever the registry carried
 * neither a name nor a slug, which is most projects created by the desktop's
 * git-remote binding. The remote is a real name the server already sends to
 * active members; only fall back to a shortened id when there is nothing at all.
 */
export function projectDisplayName(project: TeamProject): string {
  const name = project.name?.trim();
  if (name) return name;
  const slug = project.slug?.trim();
  if (slug) return slug;
  const repository = repositoryNameFromRemote(project.remoteUrl);
  if (repository) return repository;
  return `Project ${project.projectId.slice(0, 8)}`;
}

/**
 * Secondary line for a project card. Null when it would only restate the
 * headline, which is what made the old `slug || projectId` subtitle print the
 * UUID twice.
 */
export function projectSubtitle(project: TeamProject): string | null {
  const title = projectDisplayName(project);
  const slug = project.slug?.trim();
  if (slug && slug !== title) return slug;
  const repository = repositoryNameFromRemote(project.remoteUrl);
  if (repository && repository !== title) return repository;
  return null;
}

// ── Per-project access ─────────────────────────────────────────

export type ProjectRole = 'project-admin' | 'project-editor' | 'project-viewer';

/** Mirrors `PROJECT_ROLES` in `collabv3/src/TeamRoom.ts`. */
export const PROJECT_ROLES: readonly ProjectRole[] = ['project-admin', 'project-editor', 'project-viewer'];

export const PROJECT_ROLE_LABELS: Record<ProjectRole, string> = {
  'project-admin': 'Admin',
  'project-editor': 'Editor',
  'project-viewer': 'Viewer',
};

export interface ProjectGrant {
  userId: string;
  projectRole: string;
}

export interface ProjectAccessRow {
  member: TeamMember;
  projectRole: ProjectRole | null;
}

/**
 * Joins the project's grants onto the org roster so a grant for a member who
 * has since been removed does not render as a mystery user id, and a member
 * with no grant still gets a row an admin can grant from.
 */
export function buildProjectAccessRows(
  members: readonly TeamMember[],
  grants: readonly ProjectGrant[],
): ProjectAccessRow[] {
  const byUserId = new Map(grants.map((grant) => [grant.userId, grant.projectRole]));
  return members.map((member) => {
    const role = byUserId.get(member.memberId);
    return {
      member,
      projectRole: role && (PROJECT_ROLES as readonly string[]).includes(role) ? (role as ProjectRole) : null,
    };
  });
}
